import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useEntity } from '../../context/EntityContext';
import { getDashboardData } from '../../services/dashboardService';
import { DashboardDataResponse } from '../../types/dashboard';

const Dashboard: React.FC = () => {
  const { selectedEntity } = useEntity();
  const [dashboardData, setDashboardData] = useState<DashboardDataResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    async function fetchData() {
      if (!selectedEntity) return;
      setLoading(true);
      try {
        const data = await getDashboardData(selectedEntity);
        setDashboardData(data);
      } catch (error) {
        console.error("Erro ao buscar dados da dashboard:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [selectedEntity]);

  if (loading || !dashboardData) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Dashboard – {dashboardData.configuration_name}
      </Typography>

      <Grid container spacing={3}>
        {/* Informações da configuração */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Endereço:
            </Typography>
            <Typography variant="body1">
              {dashboardData.address.street}
              <br />
              CEP: {dashboardData.address.cep}
            </Typography>
          </Paper>
        </Grid>

        {/* Total de atas */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom>
              Total de Atas
            </Typography>
            <Typography variant="h3" color="primary">
              {dashboardData.total_minutes}
            </Typography>
          </Paper>
        </Grid>

        {/* Média de palavras */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom>
              Média de Palavras por Ata
            </Typography>
            <Typography variant="h3" color="secondary">
              {dashboardData.average_word_count.toFixed(0)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Atas Recentes */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Atas Recentes
        </Typography>
        {dashboardData.recent_minutes.length === 0 ? (
          <Typography>Nenhuma ata recente encontrada.</Typography>
        ) : (
          <Paper sx={{ p: 2 }}>
            {dashboardData.recent_minutes.map((m) => (
              <Box key={m.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  {m.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {m.summary}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Criada em: {new Date(m.created_at).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* Agrupamento de Atas por Mês */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Atas por Mês
        </Typography>
        {dashboardData.minutes_by_month.length === 0 ? (
          <Typography>Nenhuma ata registrada.</Typography>
        ) : (
          <Paper sx={{ p: 2, height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dashboardData.minutes_by_month}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tickFormatter={(month: number) => `Mês ${month}`} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#6A1B9A" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;